// @ts-nocheck
/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
// import MobiusLink from "@insite/mobius/Link"; // Foundation
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import getButtonIconField, { ButtonIcon, buttonIconOptions } from "@store/Framework/Common/Fields/Toolkit/buttonIcon";
import getLinkTypeField, { LinkType, linkTypeOptions } from "@store/Framework/Common/Fields/Toolkit/linkType";
import getLinkVariantField, { LinkVariant } from "@store/Framework/Common/Fields/Toolkit/linkVariant";
import { linkGetIconProps } from "@store/Themes/common/props/link";
import getCurrentSegmentFromUrl from "@toolkit/Framework/Common/Utilities/getCurrentSegmentFromUrl";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
/* Starter Custom Code - End */

// Foundation --->
import PinFill from "@store/Library/Components/Icons/Pin/PinFill";
import Hidden from "@insite/mobius/Hidden";
import MobiusLink, { LinkPresentationProps } from "@insite/mobius/Link";
import { linkStyles as mixinsLinkStyles } from "@store/Themes/common/mixins/link";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
// <--- Foundation

const enum fields {
    destination = "destination",
    overrideTitle = "overrideTitle",
    customCSS = "customCSS",
    // Starter Custom Code
    linkIcon = "linkIcon",
    linkIconPosition = "linkIconPosition",
    linkType = "linkType",
    linkVariant = "linkVariant",
    fileUrl = "fileUrl",
    openInNewWindow = "openInNewWindow",
    // Foundation
    icon = "icon",
    color = "color",
    hasColoredHover = "hasColoredHover",
}

/* Starter Custom Code - Start */
const buttonIconField = getButtonIconField(fields.linkIcon);
const linkTypeField = getLinkTypeField(fields.linkType);
const linkVariantField = getLinkVariantField(fields.linkVariant);
/* Starter Custom Code - End */

// Foundation --->
const iconMap = {
    none: undefined,
    pin: PinFill,
} as const;

export const enum LinksColors {
    Primary = "primary",
    Secondary = "secondary",
}
// <--- Foundation

interface OwnProps extends WidgetProps {
    fields: {
        [fields.destination]: LinkFieldValue;
        [fields.overrideTitle]: string;
        [fields.customCSS]: string;
        // Starter Custom Code
        [fields.linkIcon]: ButtonIcon;
        [fields.linkIconPosition]: "right" | "left";
        [fields.linkType]: LinkType;
        [fields.linkVariant]: LinkVariant;
        [fields.fileUrl]: string;
        [fields.openInNewWindow]: boolean;
        // Foundation
        [fields.icon]: keyof typeof iconMap;
        [fields.color]: LinksColors;
        [fields.hasColoredHover]: boolean;
    };
}

/* Starter Custom Code - Start */
export interface LinkStyles {
    wrapper?: InjectableCss;
    link?: LinkPresentationProps;
    // Foundation
    routerLink?: LinkPresentationProps;
}

const linkStyles: LinkStyles = {
    wrapper: {
        css: css`
            ${spacingStyles()}
            display: flex;
        `,
    },
    link: {
        icon: linkGetIconProps(undefined),
    },
    // Foundation
    routerLink: {
        icon: {
            iconProps: {
                color: "inherit",
                size: 28,
                css: css`
                    margin-right: ${pxToRem(5)};
                `,
            },
        },
    },
};
/* Starter Custom Code - End */

export const Link: FC<OwnProps> = ({ fields }) => {
    const link = useGetLink(fields.destination); // Starter Custom Code

    /* Starter Custom Code - Start */
    const linkType = linkTypeField.useParse(fields.linkType);
    const isFile = linkType === linkTypeOptions.file.id;
    const { url, title } = isFile ? { url: fields.fileUrl, title: getCurrentSegmentFromUrl(fields.fileUrl) } : link;

    const baseStyles: LinkStyles = useMergeStyles("link", linkStyles);
    const styles = React.useMemo(() => {
        return mergeProps<LinkStyles>(baseStyles, {
            link: mergeProps<LinkPresentationProps>(linkVariantField.parse(fields.linkVariant), {
                icon: {
                    iconProps: {
                        src: buttonIconField.parse(fields.linkIcon),
                    },
                    position: fields.linkIconPosition,
                },
            }),
        });
    }, [fields.linkIcon, fields.linkIconPosition, fields.linkVariant, baseStyles]);
    /* Starter Custom Code - End */

    if (!url) {
        return null;
    }

    // Foundation
    const icon = iconMap[fields.icon || "none"];
    const { routerLink } = styles;
    const hoverColor = fields.color === "primary" ? "secondary" : "primary";

    const routerLinkStyles = (): LinkPresentationProps => ({
        ...routerLink,
        css: fields.hasColoredHover ? mixinsLinkStyles(fields.color, hoverColor, false, false) : undefined,
    });
    // Foundation

    const customCssWrapper = {
        ...styles.wrapper, // Starter Custom Code
        css: css`
            ${styles.wrapper?.css /* Starter Custom Code */}
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <MobiusLink
                className="link"
                href={url}
                // Starter Custom Code
                target={fields.openInNewWindow ? "_blank" : "_self"}
                // Foundation
                {...routerLinkStyles()}
                icon={{
                    ...routerLink?.icon,
                    iconProps: {
                        src: icon,
                        ...routerLink?.icon?.iconProps,
                    },
                }}
            >
                {/* Foundation */}
                {icon ? (
                    <Hidden below="lg">{fields.overrideTitle || title || url}</Hidden>
                ) : (
                    fields.overrideTitle || title || url
                )}
                {/* Foundation */}
            </MobiusLink>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> /* Starter Custom Code */ = {
    component: Link,
    definition: {
        group: "Basic",
        icon: "link",
        fieldDefinitions: [
            /* Starter Custom Code - Start */
            {
                ...linkVariantField.definition,
            },
            {
                ...linkTypeField.definition,
            },
            {
                name: fields.fileUrl,
                displayName: "File",
                fieldType: "Contextual",
                editorTemplate: "FilePickerField",
                defaultValue: "",
                tab: basicTab,
                isVisible: x => x.fields.linkType === linkTypeOptions.file.id,
            },
            /* Starter Custom Code - End */
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                isRequired: true,
                tab: basicTab,
                isVisible: x => x.fields.linkType === linkTypeOptions.destination.id, // Starter Custom Code
            },
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            /* Starter Custom Code - Start */
            {
                name: fields.openInNewWindow,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
            },
            {
                ...buttonIconField.definition,
                displayName: "Icon",
            },
            {
                name: fields.linkIconPosition,
                displayName: "Icon Position",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "Right", value: "right" },
                    { displayName: "Left", value: "left" },
                ],
                defaultValue: "right",
                hideEmptyOption: true,
                isVisible: x => x.fields.linkIcon !== buttonIconOptions.none.id,
            },
            /* Starter Custom Code - End */
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },

            // Foundation --->
            {
                name: fields.icon,
                displayName: "Link Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Pin",
                        value: "pin",
                    },
                ],
                defaultValue: "none",
                fieldType: "General",
            },
            {
                name: fields.color,
                displayName: "Link Color",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Secondary",
                        value: LinksColors.Secondary,
                    },
                    {
                        displayName: "Primary",
                        value: LinksColors.Primary,
                    },
                ],
                defaultValue: LinksColors.Secondary,
                fieldType: "General",
            },
            {
                name: fields.hasColoredHover,
                displayName: "Make the link colored on hover",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            // <--- Foundation
        ],
    },
};

export default widgetModule;
